import React, {Component} from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import {change, Field, reduxForm} from 'redux-form';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {RESOURCES as loyaltyResources} from '../fuseLoyaltyResourceMap';
import {
    TextInput,
} from 'admin-on-rest';
import {checkTokenStatus} from '../shared/tokenFunctions';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import RaisedButton from 'material-ui/RaisedButton';
import ContentValid from 'material-ui/svg-icons/action/check-circle';
import ContentInvalid from 'material-ui/svg-icons/content/remove-circle';
import ContentSubmit from 'material-ui/svg-icons/image/remove-red-eye';
import FuseTheme from "../global/theme";

/**
 * A form containing options for creating a Loyalty Account Prefix + ID
 *
 */
class LoyaltyAccountIdInputForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            prefixChoices: [],
            selectedPrefix: null,
            rangeStart: '',
            rangeEnd: '',
            idTextValue: null,
            checkingId: false,
            idIsValid: false,
            idError: '',
            accountId: null,
            record: props.data
        };
        this.handleCheckClick = this.handleCheckClick.bind(this);
    }

    componentDidMount() {
        this.fetchPrefixData();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.prefixSelectValue !== this.props.prefixSelectValue) {
            this.setState({
                selectedPrefix: nextProps.prefixSelectValue,
            });
        }
    }

    /**
     * API call to retrieve list of Prefixes
     */
    fetchPrefixData() {
        // Assemble the URI
        const params = {
            filter: {},
            pagination: {page: 1, perPage: 10000},
            sort: {field: "prefix", order: "ASC"}
        };
        const resource = loyaltyResources.VipCardConfigurations.GET_LIST(params);

        // Retrieve the data and assign results to the Select choices
        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        let choices = this.addPrefixKeys(result.data.payload);
                        this.setState({
                            prefixChoices: choices
                        });
                    }
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                        console.log(error.response.data);
                    } else if (error.request) {
                        // No response
                        console.log(error.request);
                    } else {
                        console.log('Error', error.message);
                    }
                });
            });
    }

    /**
     * API call to get availability and validity of entered ID
     *
     * @param id
     */
    getIsAvailableLoyaltyId(id) {
        const params = {
            id: id
        };
        const resource = loyaltyResources.LoyaltyAccountIdAvailable.GET_ONE(params);

        // Retrieve the data
        import('../shared/functions')
            .then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'get',
                    url: resource.uri,
                    headers: functions.getGetHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        // ID is available and valid; display remainder of form
                        this.setState({
                            checkingId: false,
                            idError: '',
                            idIsValid: true,
                        });
                        this.props.dispatch(change('record-form', 'id', this.state.accountId));
                        this.props.onAddId('id', this.state.accountId);
                    }
                    if (result.data.error_message) {
                        this.setState({
                            checkingId: false,
                            idError: result.data.error_message,
                            idIsValid: false
                        });
                        this.props.dispatch(change('record-form', 'id', ''));
                        this.props.onAddId('id', '');
                    }
                }).catch((error) => {
                    if (error.response) {
                        // Server response outside 2xx
                        // console.log('Error Response: ', error.response.data);
                        if (error.response.data.error_message) {
                            this.setState({
                                checkingId: false,
                                idError: error.response.data.error_message,
                                idIsValid: false
                            });
                            this.props.dispatch(change('record-form', 'id', ''));
                            this.props.onAddId('id', '');
                        }
                    } else if (error.request) {
                        // No response
                        console.log('Error Request: ', error.request);
                    } else {
                        console.log('ID Error: ', error);
                        console.log('ID Error message: ', error.message);
                    }
                });
            });
    }

    /**
     * Add unique keys for Prefix list
     *
     * @param choices
     * @return {*}
     */
    addPrefixKeys = (choices) => {
        for (let choice of choices) {
            choice.id = choice.prefix + '-' + choice.range_start;
            choice.name = choice.prefix;
        }
        return choices;
    };

    /**
     * Stores start/end ranges as 6-digit strings
     *
     * @param prefix
     */
    setPrefixRanges(prefix) {
        const prefixObj = this.state.prefixChoices.find(p => p.prefix === prefix);
        if (prefixObj) {
            const start = '000000' + prefixObj.range_start.toString();
            const end = '000000' + prefixObj.range_end.toString();
            this.setState({
                rangeStart: start.slice(-6),
                rangeEnd: end.slice(-6)
            });
        }
    }

    /**
     * Handles change event from Prefix select
     *
     * @param event
     * @param index
     * @param value
     */
    handleSelectChange = (event, index, value) => {
        let prefix = (index) ? index : value;
        this.setState({
            selectedPrefix: prefix
        });
        this.setPrefixRanges(prefix);
    };

    /**
     * Handles change event and error messaging from ID input
     *
     * @param event
     * @param index
     * @param value
     */
    handleIdChange = (event, index, value) => {
        this.props.dispatch(change('record-form', 'id', ''));
        let error = '';
        let idEntry = (index) ? index : value;
        if (idEntry) {
            this.setState({
                accountId: '',
                idTextValue: idEntry
            });
            const id = parseInt(idEntry, 10);
            const start = parseInt(this.state.rangeStart, 10);
            const end = parseInt(this.state.rangeEnd, 10);
            if (!id || id === '') {
                error = 'ID value is required.';
                this.setState({
                    idTextValue: null
                });
            } else if (id < start || id > end) {
                error = 'ID must be within the range.';
            } else if (idEntry.length !== 6) {
                error = 'ID must be 6 digits (including any zeros).';
            }
        }
        this.setState({
            idError: error
        });
    };

    /**
     * Handles click of Check ID Availability button
     *
     * @param event
     */
    handleCheckClick = (event) => {
        event.preventDefault();
        const prefixValue = this.state.selectedPrefix;
        const idValue = this.state.idTextValue;
        if (prefixValue && idValue) {
            // Assemble prefix + id for ID check
            const idToCheck = prefixValue.toString() + idValue.toString();
            this.setState({
                accountId: idToCheck,
                checkingId: true,
                idIsValid: false
            });
            this.getIsAvailableLoyaltyId(idToCheck);
        }
    };

    render() {
        const styles = {
            prefixSelect: {
                display: 'block',
                minWidth: 300,
                maxWidth: '100%',
                overflow: 'hidden',
                verticalAlign: 'top'
            },
            idField: {
                display: 'block',
                minWidth: 400,
                maxWidth: '100%',
                overflow: 'hidden',
                verticalAlign: 'top'
            },
            fieldHeader: {
                display: 'block',
                width: '100%',
                fontSize: '16px',
                fontWeight: 'bold',
                color: FuseTheme.palette.lightTextColor,
                margin: '2em 0 0 0',
            },
            fieldInstructions: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                fontWeight: 'bold',
                color: FuseTheme.palette.lightTextColor,
                margin: '0.5em 0 0 0',
            },
            fieldDescription: {
                flex: '1 1 40%',
                display: 'inline-block',
                width: 'auto',
                height: '2em',
                fontSize: '12px',
                color: FuseTheme.palette.lightTextColor,
                verticalAlign: 'bottom',
            },
            error: {
                display: 'block',
                width: 'auto',
                fontSize: '12px',
                color: FuseTheme.palette.accent1Color,
            },
            icon: {display: 'inline-block', fontSize: 24, marginLeft: 8, verticalAlign: 'sub'},
            iconValid: {color: FuseTheme.palette.correctColor},
            iconInvalid: {color: FuseTheme.palette.accent1Color},
            break: {display: 'block', width: '100%', height: 0, border: 'none', margin: 0},
            paddingBottom: {paddingBottom: '1em'},
        };
        return (
            <div>
                {(this.state.prefixChoices.length > 0) &&
                <div>
                    <Field name="prefix_select"
                           component={VirtualizedSelectInput}
                           choices={this.state.prefixChoices}
                           label="Select a Loyalty Account Prefix*"
                           optionValue="prefix"
                           onChange={this.handleSelectChange}
                           elStyle={styles.prefixSelect}
                           options={{
                               clearable: false,
                               placeholder: "Select a Loyalty Account Prefix",
                           }}
                    />
                    <span style={styles.fieldDescription}>
                        Select a <strong>Prefix</strong> from the available VIP Card Configurations.
                        <TooltipIcon dataFor="ttPrefix"/>
                        <ReactTooltip id="ttPrefix" type="light" effect="solid" multiline={true} border={true}
                                      className="tooltip">
                            A <strong>Prefix</strong> is the first 7 characters of a Loyalty Account ID number.
                        </ReactTooltip>
                    </span>
                </div>
                }
                {this.state.selectedPrefix && this.state.rangeStart && this.state.rangeEnd &&
                <div>
                    <span style={styles.fieldInstructions}>
                        The {this.state.selectedPrefix} Prefix includes a range
                        of {this.state.rangeStart} through {this.state.rangeEnd}.
                        <br/>
                        <br/>
                        Enter a 6-digit ID number that falls within this range.
                        <TooltipIcon dataFor="ttId"/>
                        <ReactTooltip id="ttId" type="light" effect="solid" multiline={true} border={true}
                                      className="tooltip">
                            An <strong>Id</strong> number is the last 6 characters of a Loyalty Account ID number.
                            It must be within the number range available for this Prefix. Once a number is entered,
                            click the Check ID Availability button. If not available, try a new number.
                        </ReactTooltip>
                    </span>
                    <Field name="id_string"
                           component={TextInput}
                           label="Enter an ID number within the range*"
                           onChange={this.handleIdChange}
                           elStyle={styles.idField}
                    />
                    <span style={styles.error}>{this.state.idError}</span>

                    <hr style={{...styles.break, ...styles.paddingBottom}}/>
                    {this.state.idTextValue &&
                    <RaisedButton
                        type="button"
                        icon={<ContentSubmit/>}
                        label="Check ID Availability"
                        disabled={this.state.idError !== ''}
                        onClick={this.handleCheckClick}
                        primary/>
                    }
                    {this.state.checkingId &&
                    <span style={styles.fieldInstructions}>
                        Checking availability of Loyalty Account ID {this.state.accountId}...
                    </span>
                    }
                    {!this.state.checkingId && this.state.accountId && this.state.idIsValid &&
                    <span style={styles.fieldInstructions}>
                        Loyalty Account ID {this.state.accountId} is valid.
                        <ContentValid style={{...styles.icon, ...styles.iconValid}}/>
                    </span>
                    }
                    {!this.state.checkingId && this.state.accountId && !this.state.idIsValid &&
                    <span style={styles.fieldInstructions}>
                        Loyalty Account ID {this.state.accountId} is not available or invalid.
                        <ContentInvalid style={{...styles.icon, ...styles.iconInvalid}}/>
                    </span>
                    }
                </div>
                }
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let form = state.form['record-form'];
    if (state.admin.resources['VipCardConfigurations'] && state.form) {
        if (form && form.values) {
            const selectValue = (form.values['prefix_select']) ? form.values['prefix_select'].toString() : null;
            return {
                prefixSelectValue: selectValue,
            }
        }
    }
    return {
        prefixSelectValue: null,
    }
};

LoyaltyAccountIdInputForm.propTypes = {
    addField: PropTypes.bool,
    handleCheckClick: PropTypes.func,
    onAddId: PropTypes.func,
    prefixSelectValue: PropTypes.string,
    record: PropTypes.object,
    resource: PropTypes.string,
    submitOnEnter: PropTypes.bool,
};
LoyaltyAccountIdInputForm.defaultProps = {
    addField: true,
    prefixSelectValue: null,
    submitOnEnter: false,
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'record-form',
        enableReinitialize: true,
    }),
);

export default enhance(LoyaltyAccountIdInputForm);



// WEBPACK FOOTER //
// ./src/Fuse/components/LoyaltyAccountIdInputForm.js